// frontend\src\components\Forms\AddPayForm.js

import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, InputGroup, Alert } from 'react-bootstrap';
import { handleError } from '../../utils/errorHandling';
import { sanitizeInput, sanitizeNumeric } from '../../utils/sanitize';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Collapse from 'react-bootstrap/Collapse';
import { formatDate } from '../../utils/dateUtils';
import { addPay } from '../../services/PayService';
import {getBankAccounts } from '../../services/BankAccountService';
import { v4 as uuidv4 } from 'uuid';

const validateAmount = (amount) => {
  const numAmount = parseFloat(amount);
  if (!isNaN(numAmount) &&
      numAmount > 0 &&
      numAmount < 1000000 &&
      amount.toString().length < 10) {
    return undefined;
  }
  return 'Invalid amount';
};

const validatePayDate = (date) => {
  const now = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  if (date <= maxDate) {
    return undefined;
  }
  return 'Please enter a date less than 2 years from now';
};

const validatePayFrequency = (frequency) => {
  if (['weekly', 'fortnightly', 'monthly'].includes(frequency)) {
    return undefined;
  }
  return 'Invalid frequency';
};

const validatePayName = (name) => {
  if (name.trim().length > 0 && name.trim().length < 100) {
    return undefined;
  }
  return 'Name is too long or empty';
};

const validateRequiredFields = (data) => {
  const errors = {};
  if (!data.payName?.trim()) errors.payName = 'Pay name is required';
  if (!data.payAmount || data.payAmount <= 0) errors.amount = 'Valid amount required';
  if (!data.nextPay) errors.nextPay = 'Next pay date is required';
  if (!data.payFrequency) errors.frequency = 'Frequency is required';
  return errors;
};

const validateFrequencyRules = (data) => {
  const errors = {};
  if (!data.numPays || data.numPays < 1) {
    errors.numPays = 'Number of pays must be at least 1';
  }
  if (data.payFrequency === 'monthly') {
    if (!data.monthlyScheduleMode) {
      errors.monthlyScheduleMode = 'Select how monthly pays are scheduled';
    }
    if (data.monthlyScheduleMode === 'by_weekday' && !data.payDay) {
      errors.payDay = 'Please select a pay day';
    }
    if (data.monthlyScheduleMode === 'by_calendar_day') {
      const dom = parseInt(data.monthlyCalendarDay, 10);
      if (!data.monthlyCalendarDay || Number.isNaN(dom) || dom < 1 || dom > 28) {
        errors.monthlyCalendarDay = 'Select a calendar day between 1 and 28';
      }
    }
  } else if (data.payFrequency && !data.payDay) {
    errors.payDay = 'Please select a pay day';
  }
  return errors;
};

const validateLengths = (data) => {
  const errors = {};
  if (data.payName.length > 100) errors.payName = 'Pay name too long';
  return errors;
};


// The component
const AddPayForm = ({ onSuccess }) => {
  const [payName, setPayName] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payFrequency, setPayFrequency] = useState('');
  const [monthlyScheduleMode, setMonthlyScheduleMode] = useState('by_weekday');
  const [monthlyCalendarDay, setMonthlyCalendarDay] = useState('');
  const [payDay, setPayDay] = useState('');
  const [nextPay, setNextPay] = useState('');
  const [numPays, setNumPays] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transferOptions, setTransferOptions] = useState({});
  const [fixedAmounts, setFixedAmounts] = useState({});
  const [paySeriesId, setPaySeriesId] = useState('');
  const [lastSubmitTime, setLastSubmitTime] = useState(Date.now());
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const submitLockRef = useRef(false);


  useEffect(() => {
    // Fetch bank accounts from the API
    getBankAccounts()
      .then((accounts) => {
        setBankAccounts(accounts);
        // Initialize transfer options and fixed amounts
        const initialTransferOptions = {};
        const initialFixedAmounts = {};
        accounts.forEach((account) => {
          initialTransferOptions[account.id] = '';
          initialFixedAmounts[account.id] = 0;
        });
        setTransferOptions(initialTransferOptions);
        setFixedAmounts(initialFixedAmounts);
      })
      .catch((error) => {
        const message = handleError(error, 'AddPayForm');
        setErrorMessage(message);
      });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    switch(name) {
      case 'payName':
        setPayName(sanitizeInput(value));
        break;
      case 'payAmount':
        setPayAmount(sanitizeNumeric(value));
        break;
      case 'payFrequency':
        setPayFrequency(value);
        setNextPay('');
        if (value === 'monthly') {
          setMonthlyScheduleMode('by_weekday');
        } else {
          setMonthlyScheduleMode('by_weekday');
          setMonthlyCalendarDay('');
        }
        break;
      case 'payDay':
        setPayDay(value);
        break;
      case 'numPays':
        // Only allow positive integers - first sanitize, then ensure it's at least 1
        const numericValue = sanitizeNumeric(value);
        const parsedValue = parseInt(numericValue) || 0;
        setNumPays(Math.max(1, parsedValue));
        break;
      default:
        break;
    }
  };

  const validateForm = (data) => {
    const errors = {};

    const amountError = validateAmount(data.payAmount);
    if (amountError) errors.amount = amountError;

    const dateError = validatePayDate(data.nextPay);
    if (dateError) errors.nextPay = dateError;

    const frequencyError = validatePayFrequency(data.payFrequency);
    if (frequencyError) errors.frequency = frequencyError;

    const nameError = validatePayName(data.payName);
    if (nameError) errors.name = nameError;

    // Only merge other validations if they have errors
    const requiredErrors = validateRequiredFields(data);
    if (Object.keys(requiredErrors).length > 0) Object.assign(errors, requiredErrors);

    const frequencyRuleErrors = validateFrequencyRules(data);
    if (Object.keys(frequencyRuleErrors).length > 0) Object.assign(errors, frequencyRuleErrors);

    const lengthErrors = validateLengths(data);
    if (Object.keys(lengthErrors).length > 0) Object.assign(errors, lengthErrors);

    return errors;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (submitLockRef.current) {
      return;
    }
    if (Date.now() - lastSubmitTime < 1000) {
      setErrorMessage('Please wait before submitting again');
      return;
    }
    setLastSubmitTime(Date.now());

    const formData = {
      payName,
      payAmount,
      payFrequency,
      payDay,
      nextPay,
      numPays,
      monthlyScheduleMode: payFrequency === 'monthly' ? monthlyScheduleMode : '',
      monthlyCalendarDay: payFrequency === 'monthly' ? monthlyCalendarDay : '',
    };

    const errors = validateForm(formData);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setErrorMessage('Please check your input');
      return;
    }

    const newPaySeriesId = uuidv4();

    const payBankAccounts = bankAccounts.map((bankAccount) => ({
      bank_account: bankAccount.id,
      transfer_option: 'fixed',
      transfer_amount: 0.00,
    }));

    const newPay = {
      nickname: sanitizeInput(payName),
      amount: sanitizeNumeric(payAmount),
      frequency: payFrequency,
      next_pay: formatDate(nextPay),
      num_pays: numPays,
      pay_series_id: newPaySeriesId,
      bank_accounts: payBankAccounts,
    };
    if (payFrequency === 'monthly') {
      newPay.monthly_schedule_mode = monthlyScheduleMode;
      if (monthlyScheduleMode === 'by_calendar_day') {
        newPay.monthly_calendar_day = parseInt(monthlyCalendarDay, 10);
        newPay.pay_day = null;
      } else {
        newPay.pay_day = payDay;
        newPay.monthly_calendar_day = null;
      }
    } else {
      newPay.pay_day = payDay;
    }

    try {
      submitLockRef.current = true;
      await addPay(newPay);
      resetForm();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message = handleError(error, 'AddPayForm');
      setErrorMessage(message);
    } finally {
      submitLockRef.current = false;
    }
  };

  const resetForm = () => {
    setPayName('');
    setPayAmount('');
    setPayFrequency('');
    setMonthlyScheduleMode('by_weekday');
    setMonthlyCalendarDay('');
    setPayDay('');
    setNextPay('');
    setNumPays(1);
    setTransferOptions({});
    setFixedAmounts({});
    setErrorMessage('');
  };

  const filterDates = (date) => {
    if (payFrequency === 'monthly' && monthlyScheduleMode === 'by_calendar_day') {
      const dom = parseInt(monthlyCalendarDay, 10);
      if (!dom || dom < 1 || dom > 28) {
        return false;
      }
      return date.getDate() === dom;
    }
    if (!payDay) {
      return false;
    }
    const dayOfWeek = date.getDay();
    const selectedDayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(payDay);
    return dayOfWeek === selectedDayIndex;
  };

  const showDatePicker =
    (['weekly', 'fortnightly'].includes(payFrequency) && payDay) ||
    (payFrequency === 'monthly' && monthlyScheduleMode === 'by_weekday' && payDay) ||
    (payFrequency === 'monthly' && monthlyScheduleMode === 'by_calendar_day' && monthlyCalendarDay);

  return (
    <Form onSubmit={handleFormSubmit}>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        {Object.keys(validationErrors).map(key => (
          <Alert key={key} variant="danger">
            {key}: {validationErrors[key]}
          </Alert>
        ))}
        <Form.Group className="mb-3" controlId="payName">
        <Form.Label>Pay Nickname:</Form.Label>
        <Form.Control
          type="text"
          value={payName}
          onChange={(e) => setPayName(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="payAmount">
        <Form.Label>Amount:</Form.Label>
        <InputGroup>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            type="number"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            placeholder="Enter amount"
          />
        </InputGroup>
      </Form.Group>

      <Form.Group className="mb-3" controlId="payFrequency">
        <Form.Label>Frequency of Pay:</Form.Label>
        <Form.Select name="payFrequency" value={payFrequency} onChange={handleInputChange}>
          <option value="">Select frequency</option>
          <option value="weekly">Weekly</option>
          <option value="fortnightly">Fortnightly</option>
          <option value="monthly">Monthly</option>
        </Form.Select>
      </Form.Group>

      {payFrequency === 'monthly' && (
        <Form.Group className="mb-3" controlId="monthlyScheduleMode">
          <Form.Label>Monthly schedule</Form.Label>
          <div>
            <Form.Check
              type="radio"
              id="msm-weekday"
              name="monthlyScheduleMode"
              label="Same weekday position each month (e.g. 2nd Thursday)"
              checked={monthlyScheduleMode === 'by_weekday'}
              onChange={() => {
                setMonthlyScheduleMode('by_weekday');
                setMonthlyCalendarDay('');
                setNextPay('');
              }}
            />
            <Form.Check
              type="radio"
              id="msm-dom"
              name="monthlyScheduleMode"
              label="Same calendar day each month (1-28 only)"
              checked={monthlyScheduleMode === 'by_calendar_day'}
              onChange={() => {
                setMonthlyScheduleMode('by_calendar_day');
                setPayDay('');
                setNextPay('');
              }}
            />
          </div>
        </Form.Group>
      )}

      {payFrequency === 'monthly' && monthlyScheduleMode === 'by_calendar_day' && (
        <Form.Group className="mb-3" controlId="monthlyCalendarDay">
          <Form.Label>Day of month</Form.Label>
          <Form.Select
            value={monthlyCalendarDay}
            onChange={(e) => {
              setMonthlyCalendarDay(e.target.value);
              setNextPay('');
            }}
          >
            <option value="">Select day (1-28)</option>
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={String(d)}>{d}</option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      {(payFrequency && payFrequency !== 'monthly') || (payFrequency === 'monthly' && monthlyScheduleMode === 'by_weekday') ? (
      <Form.Group className="mb-3" controlId="payDay">
        <Form.Label>Pay day (weekday)</Form.Label>
        <Form.Select name="payDay" value={payDay} onChange={handleInputChange}>
          <option value="">Select pay day</option>
          <option value="Mon">Monday</option>
          <option value="Tue">Tuesday</option>
          <option value="Wed">Wednesday</option>
          <option value="Thu">Thursday</option>
          <option value="Fri">Friday</option>
          <option value="Sat">Saturday</option>
          <option value="Sun">Sunday</option>
        </Form.Select>
      </Form.Group>
      ) : null}

      {showDatePicker && (
        <Form.Group className="mb-3" controlId="payDueDate">
          <Form.Label>Date of next pay:</Form.Label>
          <br />
          <DatePicker
            selected={nextPay}
            onChange={(date) => setNextPay(date)}
            dateFormat="yyyy-MM-dd"
            className="form-control"
            filterDate={filterDates}
          />
        </Form.Group>
      )}

      <Form.Group className="mb-3" controlId="numPays">
        <Form.Label>Number of pays:</Form.Label>
        <InputGroup>
          <Form.Control
            type="number"
            name="numPays"
            value={numPays}
            onChange={handleInputChange}
            placeholder="Enter number"
          />
        </InputGroup>
      </Form.Group>

      <Button variant="primary" type="submit">
        Add Pay
      </Button>
    </Form>
  );
};

export default AddPayForm;
